import { useCallback, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorNotice, PageHead } from '../../ui/kit';
import { useCurrency, useTenantDetails } from '../../api/hooks';
import { useTenant } from '../../auth/session';
import { fmtMoney, today } from '../../lib/format';
import type { Money } from '@mybooks/ledger';
import { ExportButton, ReportTableView, type ServerPdf } from './ReportView';
import type { ReportTable } from './model';
import type { ReportMeta } from './catalog';
import { DATE_PRESETS, RANGE_PRESETS, dateFor, datePresetOf, isValidDate, presetOf, rangeFor, type DatePreset, type Range, type RangePreset } from './period';
import { DateInput } from '../../ui/DateInput';

/** The business name as the reports print it. */
export function useBusinessName(): string {
  const tenant = useTenant();
  const details = useTenantDetails();
  return details.data?.name ?? tenant.name;
}

/** Search params as report state, updated in place so the back button leaves the report. */
export function useReportParams() {
  const [params, setParams] = useSearchParams();
  const set = useCallback((patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) { if (v === null || v === '') next.delete(k); else next.set(k, v); }
      return next;
    }, { replace: true });
  }, [setParams]);
  return { params, set };
}

/** A from/to period from the URL, falling back to a preset. `error` is set when the dates make no sense. */
export function useRange(fallback: RangePreset) {
  const { params, set } = useReportParams();
  const def = rangeFor(fallback, today());
  const from = params.get('from') ?? def.from;
  const to = params.get('to') ?? def.to;
  const error = !isValidDate(from) || !isValidDate(to) ? 'Enter both dates' : to < from ? 'The end date is before the start date' : null;
  return { range: { from, to } as Range, error, set };
}
export function useAsOf() {
  const { params, set } = useReportParams();
  const asOf = params.get('as_of') ?? today();
  return { asOf, error: isValidDate(asOf) ? null : 'Enter a date', set };
}

export function RangeFields({ range, error, set, allowAll }: { range: Range; error: string | null; set: (p: Record<string, string | null>) => void; allowAll?: boolean }) {
  const preset = presetOf(range, today());
  const presets = RANGE_PRESETS.filter(([p]) => allowAll || p !== 'all');
  return (
    <>
      <div className="field">
        <label htmlFor="rp-preset">Date range</label>
        <select className="input" id="rp-preset" style={{ width: 'auto' }} value={preset ?? 'custom'}
          onChange={(e) => { if (e.target.value !== 'custom') { const r = rangeFor(e.target.value as RangePreset, today()); set({ from: r.from, to: r.to }); } }}>
          {presets.map(([p, label]) => <option key={p} value={p}>{label}</option>)}
          <option value="custom" disabled={preset !== null}>Custom</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="rp-from">From</label>
        <DateInput className={`input ${error ? 'bad' : ''}`} id="rp-from" value={range.from} onChange={(e) => set({ from: e.target.value })} aria-describedby={error ? 'rp-error' : undefined} />
      </div>
      <div className="field">
        <label htmlFor="rp-to">To</label>
        <DateInput className={`input ${error ? 'bad' : ''}`} id="rp-to" value={range.to} onChange={(e) => set({ to: e.target.value })} aria-describedby={error ? 'rp-error' : undefined} />
      </div>
      {error && <span className="field"><span className="error" id="rp-error" role="alert">{error}</span></span>}
    </>
  );
}

export function AsOfFields({ asOf, error, set }: { asOf: string; error: string | null; set: (p: Record<string, string | null>) => void }) {
  const preset = datePresetOf(asOf, today());
  return (
    <>
      <div className="field">
        <label htmlFor="rp-preset">As of</label>
        <select className="input" id="rp-preset" style={{ width: 'auto' }} value={preset ?? 'custom'}
          onChange={(e) => { if (e.target.value !== 'custom') set({ as_of: dateFor(e.target.value as DatePreset, today()) }); }}>
          {DATE_PRESETS.map(([p, label]) => <option key={p} value={p}>{label}</option>)}
          <option value="custom" disabled={preset !== null}>Custom</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor="rp-asof">Date</label>
        <DateInput className={`input ${error ? 'bad' : ''}`} id="rp-asof" value={asOf} onChange={(e) => set({ as_of: e.target.value })} />
      </div>
      {error && <span className="field"><span className="error" role="alert">{error}</span></span>}
    </>
  );
}

/** The prototype's check pills: green when two figures agree, red with the difference when not. */
export function CheckPill({ diff, ok, currency }: { diff: Money; ok: string; currency: string }) {
  return diff === 0n ? <span className="pill done">{ok}</span> : <span className="pill overdue">Out by {fmtMoney(diff < 0n ? -diff : diff, currency)}</span>;
}

export interface FrameProps {
  meta: ReportMeta;
  /** "Jan 1 – Oct 15, 2026", "As of Oct 15, 2026". */
  period: string;
  controls: ReactNode;
  state: { loading: boolean; error: unknown; retry: () => void } | null;
  table: ReportTable | null;
  /** Shown above the table, like the prototype's statement and ledger pickers' description. */
  head?: ReactNode;
  /** The statements' title block (profit and loss, balance sheet). */
  exTitle?: string;
  note?: ReactNode;
  empty?: ReactNode;
  compact?: boolean;
  fileTag?: string;
  /** The API's PDF of this report, for Export → PDF. */
  pdf?: ServerPdf | null;
  /** The rows shown when they are a page of `table`; exports always take the whole table. */
  view?: ReportTable | null;
  /** Under the table, such as a pager. */
  after?: ReactNode;
}

/** The page around every report: crumb, title, Export, controls card, then the report body. */
export function ReportFrame({ meta, period, controls, state, table, head, exTitle, note, empty, compact, fileTag, pdf, view, after }: FrameProps) {
  const business = useBusinessName();
  const currency = useCurrency();
  let body: ReactNode;
  if (state?.loading) body = <div className="empty" aria-busy="true">Loading…</div>;
  else if (state?.error) body = <ErrorNotice error={state.error} retry={state.retry} />;
  else if (!table) body = empty ?? <div className="empty">Choose the options above to run this report.</div>;
  else body = (
    <>
      {exTitle && <div className="ex-title"><h2>{meta.name}</h2><div>{business}</div><div className="muted">{exTitle}</div></div>}
      {table.rows.length === 0 && empty ? empty : <div className="table-wrap"><ReportTableView table={view ?? table} currency={currency} /></div>}
      {table.rows.length > 0 && after}
      {note && <div className="report-note">{note}</div>}
    </>
  );
  return (
    <>
      <PageHead crumb={{ to: '/reports', label: 'Reports' }} title={meta.name} sub={`${business} · ${period}`}
        actions={<ExportButton source={{ title: meta.name, business, period, table: state?.loading || state?.error ? null : table, currency, fileTag: fileTag ?? period, pdf: pdf ?? null }} />} />
      <div className="card ex-controls ledger-controls">{controls}</div>
      {head}
      <div className={`card report-body ${exTitle ? 'ex-report' : ''} ${compact ? 'compact' : ''}`} aria-live="polite">{body}</div>
    </>
  );
}

